import React, { useState } from 'react';
import { api } from '../../shared/utils/api';

const REASONS = [
  { value: 'wrong_answer', label: 'The correct answer seems wrong' },
  { value: 'poor_explanation', label: 'The explanation is unclear or incorrect' },
  { value: 'ambiguous', label: 'The question is confusingly worded' },
  { value: 'outdated_law', label: 'There are multiple correct answers' },
  { value: 'other', label: 'Other issue' },
];

export default function ReportPanel({ questionId, onClose }) {
  const [reason, setReason] = useState('wrong_answer');
  const [detail, setDetail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    if (sending) return;
    setSending(true);
    try {
      await api(`/api/questions/${questionId}/report`, {
        method: 'POST',
        body: JSON.stringify({ reason, detail }),
      });
      setSent(true);
    } catch {
      // silently fail
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="border border-green rounded-r-lg overflow-hidden mb-sp-3">
        <div className="p-sp-5 bg-bg-card">
          <div className="text-center py-sp-5">
            <div className="w-10 h-10 rounded-full bg-[#f0fdf4] flex items-center justify-center mx-auto mb-sp-3">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--green)"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <p className="text-sm font-semibold text-text-primary mb-sp-1">Thanks for reporting</p>
            <p className="text-sm text-text-muted max-w-xs mx-auto">
              We'll review this question and take action if needed. Your feedback helps improve the
              question bank.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-border-soft rounded-r-lg overflow-hidden mb-sp-3">
      <div className="bg-bg-surface px-sp-4 py-sp-3 flex items-center justify-between">
        <span className="text-sm font-semibold text-text-primary">
          Report a problem with this question
        </span>
        <button
          onClick={onClose}
          className="bg-transparent border-0 text-text-muted cursor-pointer text-lg leading-none"
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <div className="p-sp-4 bg-bg-card">
        {REASONS.map((r) => {
          const active = reason === r.value;
          return (
            <div
              key={r.value}
              onClick={() => setReason(r.value)}
              className={`flex items-center gap-sp-3 px-sp-3 py-sp-3 rounded-r-md border mb-sp-2 cursor-pointer transition-colors ${
                active
                  ? 'border-accent bg-blue-lightest'
                  : 'border-border-soft hover:border-border-medium'
              }`}
            >
              <span
                className={`w-[18px] h-[18px] rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                  active ? 'border-accent' : 'border-border-soft'
                }`}
              >
                {active && <span className="w-[10px] h-[10px] rounded-full bg-accent" />}
              </span>
              <p className="text-sm text-text-primary">{r.label}</p>
            </div>
          );
        })}
        <div className="mt-sp-3">
          <textarea
            placeholder="Add detail (optional)..."
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            className="w-full border border-border-soft rounded-r-md px-sp-4 py-sp-3 text-sm font-sans resize-y min-h-[60px] outline-none focus:border-accent bg-bg-card text-text-primary placeholder:text-text-muted"
          />
        </div>
        <div className="mt-sp-3 flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={sending}
            className="px-sp-3 py-sp-2 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {sending ? 'Sending…' : 'Submit report'}
          </button>
        </div>
      </div>
    </div>
  );
}
